import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Chip, Divider, FormHelperText, InputLabel } from "@mui/material";
import { isArray, isEmpty, isNil } from "lodash";
import { useState } from "react";
import { Field, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanFetchedDataUwRequirementsSet } from "../../redux/reducers/loanFetchedDataReducer";
import CreateEditForm from "../finalForm/CreateEditForm";
import RffBasicHtmlEditor from "../finalForm/inputs/RffBasicHtmlEditor";
import RffDateField from "../finalForm/inputs/RffDateField";
import RffSelectDocGroupTypeField from "../finalForm/inputs/RffSelectDocGroupTypeField";
import RffSelectField from "../finalForm/inputs/RffSelectField";
import RffSelectToggleField from "../finalForm/inputs/RffSelectToggleField";
import RffSelectUserEmailField from "../finalForm/inputs/RffSelectUserEmailField";
import RffTextareaField from "../finalForm/inputs/RffTextareaField";
import RffGroup from "../finalForm/shared/RffGroup";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import RffBooleanSwitchField from "../finalForm/inputs/RffBooleanSwitchField";
import RffFormSection from "../finalForm/shared/RffFormSection";
import RffRowGroup from "../finalForm/shared/RffRowGroup";
import RffBooleanSwitch from "../finalForm/inputs/RffBooleanSwitch";
import RffBooleanToggleField from "../finalForm/inputs/RffBooleanToggleField";
import RffBooleanCheckbox from "../finalForm/inputs/RffBooleanCheckbox";
import SidebarSection from "../sidebar/shared/SidebarSection";
import SectionCard from "../layout/SectionCard";
import RffInputWrapper from "../finalForm/shared/RffInputWrapper";
import YesNoSwitch from "../inputs/YesNoSwitch";
import {
  CONDITION_GATE,
  CONDITION_GATE_OPTIONS,
} from "../../constants/selectOptions/selectOptionsConditionGate";

export const PRIORITY_OPTIONS = ["Low", "Medium", "High"];

export const STATUS_OPTIONS = [
  "Open",
  "In Progress",
  "Accepted",
  "Rejected",
  "Second Level Review",
];

export const DOC_STIP_STATUS_OPTIONS = [
  "Open Item",
  "Received - Pending Review",
  "Reviewed - Not Accepted",
  "Accepted",
];

// `requirementType` is no longer picked directly — it is driven by the
// "Includes Document" switch (Document when on, Condition when off). "Stip" is
// retired: it is not offered anywhere, but legacy records that still carry it
// keep rendering as-is in the checklist/table views.
export const REQUIREMENT_TYPE_OPTIONS = ["Condition", "Document"];

const REQUIREMENT_TYPE_DOCUMENT = "Document";
const REQUIREMENT_TYPE_CONDITION = "Condition";

/**
 * "Requires Document" — a boolean-looking control bound to the string
 * `requirementType` field. On maps to "Document" (and reveals the doc-type
 * picker), off maps to "Condition". Mirrors RffBooleanSwitchField's layout so
 * it reads like the other switches.
 */
const RequirementTypeDocumentSwitch = ({ row, disabled }) => (
  <RffInputWrapper row={row}>
    <Field
      name="requirementType"
      render={({ input }) => {
        const isDocument = input.value === REQUIREMENT_TYPE_DOCUMENT;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {/* Matches InputWrapper's label/helper pair so this reads as a
                  normal form field rather than a bespoke switch row. */}
              <InputLabel
                sx={{
                  whiteSpace: "normal",
                  fontWeight: "normal",
                  marginBottom: "3px",
                }}
              >
                Requires Document
              </InputLabel>
              <FormHelperText
                sx={{ fontStyle: "italic", fontSize: "11px !important", m: 0 }}
              >
                If enabled, a specific document is required to satisfy this
                condition
              </FormHelperText>
            </Box>
            <YesNoSwitch
              checked={isDocument}
              disabled={disabled}
              onChange={(nextValue) =>
                input.onChange(
                  nextValue
                    ? REQUIREMENT_TYPE_DOCUMENT
                    : REQUIREMENT_TYPE_CONDITION,
                )
              }
            />
          </Box>
        );
      }}
    />
  </RffInputWrapper>
);

const CreateUpdateLoanConditionForm = ({
  initialValues,
  onSuccessFn,
  onUpdateCancel,
  isUpdating,
}) => {
  const dispatch = useDispatch();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { clearFetchSetLoanDrilldown } = useReduxLoanDrilldownHook();

  const {
    loading,
    createLoanUnderwritingRequirement,
    createLoanUnderwritingRequirementWithFile,
    updateLoanUnderwritingRequirement,
  } = useChecklistRouteHook();

  const handleOnCreate = (values) => {
    const dataSpread = { ...values };

    if (dataSpread?.requirementType === "Document") {
      dataSpread.docStipStatus = "Open Item";
    }

    const requestParams = {
      dealId: loanDrilldown?._id,
      data: dataSpread,
      onSuccessFn: (response) => {
        dispatch(loanFetchedDataUwRequirementsSet(response));
        if (onSuccessFn) onSuccessFn(response);
      },
    };

    if (isEmpty(values?.uploadMetadata)) {
      createLoanUnderwritingRequirement(requestParams);
    } else {
      createLoanUnderwritingRequirementWithFile({
        dealId: loanDrilldown?._id,
        data: values,
        onSuccessFn: (response) => {
          clearFetchSetLoanDrilldown({ loanId: loanDrilldown?._id });
          if (onSuccessFn) onSuccessFn(response);
        },
      });
    }
  };

  const handleOnUpdate = (values) => {
    const data = { ...values };
    const requirementType =
      data.requirementType ?? initialValues?.requirementType;

    if (data.status === "Accepted" && requirementType === "Document") {
      data.docStipStatus = "Accepted";
    }

    updateLoanUnderwritingRequirement({
      dealId: loanDrilldown?._id,
      requirementId: initialValues._id,
      data,
      onSuccessFn: (response) => {
        if (isArray(response)) {
          dispatch(loanFetchedDataUwRequirementsSet(response));
          return;
        }
        const resData = response?.conditions || response?.comments;
        dispatch(loanFetchedDataUwRequirementsSet(resData));
      },
    });
  };

  if (isUpdating) {
    return (
      <CreateEditForm
        onUpdate={handleOnUpdate}
        loading={loading}
        initialValues={initialValues}
        updateButtonText="Update Condition"
      >
        <StipFormEditQuestions editing />
      </CreateEditForm>
    );
  }
  return (
    <CreateEditForm
      onCreate={handleOnCreate}
      loading={loading}
      // formSpy
      initialValues={{
        displayExternally: false,
        // The "Includes Document" switch is off by default, so seed the string
        // it maps to — an untouched switch must still submit "Condition".
        requirementType: "Condition",
        ...(initialValues || {}),
      }}
      createButtonText="Create Condition"
      // createButtonText="Create Requirement"
    >
      <StipFormQuestions />
    </CreateEditForm>
  );
};

/**
 * Form Fields
 */
const StipFormQuestions = ({ editing }) => {
  const { values } = useFormState();
  const isDocument = values?.requirementType === "Document";
  const isPostClose = values?.conditionGate === CONDITION_GATE.POST_CLOSE;
  const [show, setShow] = useState(false);
  return (
    <Box
      sx={{
        ".rdw-editor-main": { minHeight: "80px" },
        ".MuiInputBase-root": { background: "#ffffff" },
      }}
    >
      <RffGroup>
        <RffSelectToggleField
          required
          name="conditionGate"
          label="Type"
          options={CONDITION_GATE_OPTIONS}
        />

        <RffBasicHtmlEditor
          label="Details"
          placeholder="Start typing requirement details here..."
        />

        <RequirementTypeDocumentSwitch />

        {isDocument && (
          <>
            <RffSelectDocGroupTypeField
              required
              label="Required Document Type"
              helperText={"Type of document required to satisfy this condition"}
            />
          </>
        )}

        {isPostClose && (
          <RffDateField
            name="dueDateTime"
            label="Due Date"
            helperText="Optional deadline for when this condition should be satisfied"
          />
        )}

        <RffBooleanSwitchField
          name="displayExternally"
          label="Share Externally"
          required
          description="If enabled, will be viewable by external users like a loan counterparty"
        />
      </RffGroup>
    </Box>
  );
};
const StipFormEditQuestions = () => {
  const { values } = useFormState();
  const isDocument = values?.requirementType === "Document";
  const isPostClose = values?.conditionGate === CONDITION_GATE.POST_CLOSE;
  const [show, setShow] = useState(false);
  return (
    <Flx
      column
      g={3}
      sx={{
        ".rdw-editor-main": { minHeight: "80px" },
        ".MuiInputBase-root": { background: "#ffffff" },

        // background: "#f8fafc",
        // boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
      }}
    >
      <SectionCard>
        <SidebarSection title="Status & Resolution" underlinePosition="title">
          <RffRowGroup suppressBottomMargin suppressBottomDivider>
            <RffSelectToggleField
              row
              name="status"
              label="Condition Status"
              options={STATUS_OPTIONS}
            />

            {(isDocument || !isNil(values?.docStipStatus)) && (
              <>
                <RffSelectField
                  row
                  name="docStipStatus"
                  label="Document Status"
                  helperText={
                    "Status of document required to satisfy condition"
                  }
                  options={DOC_STIP_STATUS_OPTIONS}
                />
              </>
            )}
            <RffSelectUserEmailField
              name="resolvedBy"
              row
              label="Resolved By"
              helperText="Email of the person responsible for resolving this condition"
            />
            <RffTextareaField
              row
              name="resolutionNotes"
              label="Resolution Notes"
              minHeight={80}
            />
          </RffRowGroup>
        </SidebarSection>
      </SectionCard>

      <SectionCard>
        <SidebarSection
          defaultOpen={false}
          title="Condition Details"
          underlinePosition="title"
        >
          <RffRowGroup suppressBottomMargin suppressBottomDivider>
            {/* Not `required` here: legacy conditions predate conditionGate, and
                requiring it would block unrelated edits on those records. */}
            <RffSelectField
              row
              name="conditionGate"
              label="Type"
              options={CONDITION_GATE_OPTIONS}
            />

            <RffBasicHtmlEditor
              label="Details"
              placeholder="Start typing requirement details here..."
            />

            <RequirementTypeDocumentSwitch row />

            {isDocument && (
              <>
                <RffSelectDocGroupTypeField
                  required
                  row
                  label="Document Type"
                  // helperText={"Type of document required for this condition"}
                  helperText={
                    "Type of document required to satisfy this condition"
                  }
                />
              </>
            )}

            {isPostClose && (
              <RffDateField
                row
                name="dueDateTime"
                label="Due Date"
                helperText="When this condition should be satisfied"
              />
            )}

            {/* <RffBooleanSwitchField */}
            <RffBooleanCheckbox
              name="displayExternally"
              label="Share Externally"
              required
              row
              helperText="If enabled, will be viewable by external users like a loan counterparty"
            />
          </RffRowGroup>
        </SidebarSection>
      </SectionCard>
    </Flx>
  );
};

export default CreateUpdateLoanConditionForm;
